import { useEffect, useRef } from 'react';
import { IDLE_STEP_MS, pollDelay } from '../helpers/poll-helper';
import { createPollCoordinator, leadAlone, PollCoordinator } from '../services/poll-coordinator';

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'mousemove', 'scroll'];

interface PollingOptions<T> {
  // Tabs with the same key take turns polling, and every tab gets each result through onData.
  shareKey?: string;
  onData?: (data: T) => void;
  // False waits a full delay before the first poll, for screens that load their own initial data.
  immediate?: boolean;
}

// Polls only while the tab is visible and online, backing off while the user is idle and while polls fail.
const usePolling = <T>(
  poll: () => Promise<T>,
  baseMs: number,
  { shareKey, onData, immediate = true }: PollingOptions<T> = {}
) => {
  const pollRef = useRef(poll);
  const onDataRef = useRef(onData);
  pollRef.current = poll;
  onDataRef.current = onData;

  useEffect(() => {
    let disposed = false;
    let timer: number;
    let running = false;
    let leading = false;
    let releaseTurn: () => void = null;
    let failures = 0;
    let lastPollAt = immediate ? -Infinity : Date.now();
    let lastActivityAt = Date.now();

    const coordinator: PollCoordinator<T> = shareKey
      ? createPollCoordinator<T>(shareKey, (data, fetchedAt) => {
        lastPollAt = Math.max(lastPollAt, fetchedAt);
        onDataRef.current?.(data);
      })
      : null;

    const canPoll = () => leading && document.visibilityState === 'visible' && navigator.onLine !== false;

    const schedule = () => {
      window.clearTimeout(timer);
      if (disposed || running || !canPoll()) return;

      const delay = pollDelay(baseMs, { idleMs: Date.now() - lastActivityAt, failures });
      timer = window.setTimeout(run, Math.max(0, lastPollAt + delay - Date.now()));
    };

    const run = async () => {
      if (running || !canPoll()) return;
      running = true;

      try {
        const data = await pollRef.current();
        failures = 0;
        lastPollAt = Date.now();
        if (disposed) return;
        onDataRef.current?.(data);
        coordinator?.share(data, lastPollAt);
      } catch {
        failures++;
        lastPollAt = Date.now();
      } finally {
        running = false;
      }

      schedule();
    };

    const takeTurn = () => {
      if (releaseTurn) return;
      const onLead = () => {
        leading = true;
        schedule();
      };
      releaseTurn = coordinator ? coordinator.lead(onLead) : leadAlone(onLead);
    };

    const giveUpTurn = () => {
      window.clearTimeout(timer);
      leading = false;
      releaseTurn?.();
      releaseTurn = null;
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        lastActivityAt = Date.now();
        takeTurn();
      } else {
        giveUpTurn();
      }
    };

    // A user back from a long idle shouldn't wait out a delay stretched while they were away.
    const onActivity = () => {
      const wasIdle = Date.now() - lastActivityAt >= IDLE_STEP_MS;
      lastActivityAt = Date.now();
      if (wasIdle) schedule();
    };

    if (document.visibilityState === 'visible') takeTurn();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('online', schedule);
    window.addEventListener('offline', schedule);
    ACTIVITY_EVENTS.forEach(type => window.addEventListener(type, onActivity, { capture: true, passive: true }));

    return () => {
      disposed = true;
      giveUpTurn();
      coordinator?.close();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('online', schedule);
      window.removeEventListener('offline', schedule);
      ACTIVITY_EVENTS.forEach(type => window.removeEventListener(type, onActivity, { capture: true }));
    };
  }, [baseMs, shareKey, immediate]);
};

export default usePolling;
